import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package,
  CheckCircle,
  Clock,
  Truck,
  Star,
  Edit,
  Printer,
  DollarSign,
  XCircle,
  Calendar,
  Search,
  ArrowRightLeft,
  Ban,
  Download,
  Upload,
  Image as ImageIcon,
  MoreVertical,
  Trash2,
  Camera
} from 'lucide-react';
import { Mic, BadgePercent, Send } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import ImageEditor from '../components/ImageEditor';
import { formatTime12 } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmed',
    color: 'bg-blue-500',
    icon: CheckCircle,
    nextStatus: 'in_progress'
  },
  in_progress: {
    label: 'Preparing',
    color: 'bg-orange-500',
    icon: Clock,
    nextStatus: 'ready'
  },
  ready: {
    label: 'Ready',
    color: 'bg-green-500',
    icon: CheckCircle,
    nextStatus: 'ready_to_deliver'
  },
  ready_to_deliver: {
    label: 'Ready to Deliver',
    color: 'bg-cyan-500',
    icon: Truck,
    nextStatus: 'picked_up'
  },
  picked_up: {
    label: 'Out for Delivery',
    color: 'bg-purple-500',
    icon: Truck,
    nextStatus: 'delivered'
  },
  reached: {
    label: 'Reached',
    color: 'bg-indigo-500',
    icon: Truck
  },
  delivered: {
    label: 'Delivered',
    color: 'bg-green-600',
    icon: Star
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-500',
    icon: XCircle
  }
};

const ManageOrders = () => {
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [photoUploadModalOpen, setPhotoUploadModalOpen] = useState(false);
  const [selectedOrderForPhoto, setSelectedOrderForPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // New state for edit functionality
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editImageEditorOpen, setEditImageEditorOpen] = useState(false);
  const [pendingEditFile, setPendingEditFile] = useState(null);

  // Discount on Ready orders (Super Admin only)
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountOrder, setDiscountOrder] = useState(null);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountSubmitting, setDiscountSubmitting] = useState(false);
  
  // Camera capture for Ready to Deliver
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraOrderId, setCameraOrderId] = useState(null);
  const [cameraOrder, setCameraOrder] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deliveryChoiceStep, setDeliveryChoiceStep] = useState(false);
  const [transferOutletStep, setTransferOutletStep] = useState(false);
  const [selectedTransferOutlet, setSelectedTransferOutlet] = useState('');
  
  // Delivery assignment
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Group B — Add Delivery wizard
  const [addDeliveryOpen, setAddDeliveryOpen] = useState(false);
  const [addDeliveryOrder, setAddDeliveryOrder] = useState(null);
  const [addDeliveryStep, setAddDeliveryStep] = useState(1);
  const [addDeliveryZones, setAddDeliveryZones] = useState([]);
  const [addDeliveryCities, setAddDeliveryCities] = useState([]);
  const [addDeliveryData, setAddDeliveryData] = useState({
    zone_id: '', delivery_charge: '', is_complementary: false,
    receiver_name: '', receiver_phone: '', delivery_address: '', delivery_city: '',
    assign_delivery_person_id: ''
  });
  const [addingDelivery, setAddingDelivery] = useState(false);

  // Delete order with reason
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOrder, setDeleteOrder] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Bulk selection for KOT
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [occasionFilter, setOccasionFilter] = useState('all');
  const [flavourFilter, setFlavourFilter] = useState('all');
  const [outletFilter, setOutletFilter] = useState('all');
  const [flavoursList, setFlavoursList] = useState([]);
  const [occasionsList, setOccasionsList] = useState([]);
  // c6: Send cake photos dialog state
  const [sendPhotosOpen, setSendPhotosOpen] = useState(false);
  const [sendPhotosOrder, setSendPhotosOrder] = useState(null);
  const [sendPhotosDate, setSendPhotosDate] = useState('');
  const [sendPhotosIncludeRef, setSendPhotosIncludeRef] = useState(true);
  const [sendPhotosIncludeActual, setSendPhotosIncludeActual] = useState(true);
  const [sendPhotosSending, setSendPhotosSending] = useState(false);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/')) return `${API_URL}/api${url}`;
    return `${API_URL}${url}`;
  };

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash'
  });

  // Local YYYY-MM-DD (IST-safe, no UTC drift near midnight)
  const localToday = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    fetchOrders();
    fetchOutlets();
    fetchDeliveryPersons();
    fetchFlavoursAndOccasions();
    // Default: show today's orders only (IST-safe local date).
    // Use "Clear Dates" / "Tomorrow" / custom From-To to widen the range.
    const t = localToday();
    setDateFrom(t);
    setDateTo(t);
  }, []);

  // Convert HH:mm 24h → "h:mm AM/PM"
  const to12Hour = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return hhmm || '';
    const [hStr, mStr] = hhmm.split(':');
    const h = Number(hStr);
    const m = Number(mStr || 0);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const ampm = h >= 12 ? 'PM' : 'AM';
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // Subtract N minutes from HH:mm and return HH:mm (24h). Clamps at 00:00.
  const subtractMinutesFromTime = (hhmm, minutes) => {
    if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return hhmm || '';
    const [hStr, mStr] = hhmm.split(':');
    const h = Number(hStr);
    const m = Number(mStr || 0);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    let total = h * 60 + m - (Number(minutes) || 0);
    if (total < 0) total = 0;
    const nh = Math.floor(total / 60) % 24;
    const nm = total % 60;
    return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
  };

  const fetchFlavoursAndOccasions = async () => {
    try {
      const [flavRes, occRes] = await Promise.all([
        axios.get(`${API_URL}/api/flavours`),
        axios.get(`${API_URL}/api/occasions`)
      ]);
      setFlavoursList(flavRes.data);
      setOccasionsList(occRes.data);
    } catch (error) {
      console.error('Failed to fetch flavours/occasions:', error);
    }
  };



  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, activeTab, dateFrom, dateTo, occasionFilter, flavourFilter, outletFilter, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/orders/manage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ type: 'error', text: 'Failed to fetch orders' });
    } finally {
      setLoading(false);
    }
  };

  const fetchOutlets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/outlets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutlets(response.data);
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  const fetchDeliveryPersons = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/delivery/persons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryPersons(response.data);
    } catch (error) {
      console.error('Error fetching delivery persons:', error);
    }
  };

  const openAssignDialog = (orderId) => {
    setAssignOrderId(orderId);
    setSelectedDeliveryPerson('');
    setAssignDialogOpen(true);
  };

  // c6: Send cake photos dialog helpers
  const openSendCakePhotosDialog = (order) => {
    setSendPhotosOrder(order);
    // Default date = today (IST-safe local)
    setSendPhotosDate(localToday());
    setSendPhotosIncludeRef(!!order.cake_image_url);
    setSendPhotosIncludeActual(!!order.actual_cake_image_url);
    setSendPhotosOpen(true);
  };

  const submitSendCakePhotos = async () => {
    if (!sendPhotosOrder) return;
    setSendPhotosSending(true);
    try {
      await axios.post(
        `${API_URL}/api/orders/${sendPhotosOrder.id}/send-cake-photos`,
        {
          date: sendPhotosDate || localToday(),
          include_reference: sendPhotosIncludeRef,
          include_actual: sendPhotosIncludeActual,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendPhotosOpen(false);
      setMessage({ type: 'success', text: 'Cake photos queued to send on WhatsApp.' });
      setTimeout(() => setMessage(null), 3500);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to send cake photos' });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setSendPhotosSending(false);
    }
  };

  const handleAssignDelivery = async () => {
    if (!selectedDeliveryPerson || !assignOrderId) return;
    setAssigning(true);
    try {
      await axios.post(
        `${API_URL}/api/delivery/assign-order/${assignOrderId}?delivery_person_id=${selectedDeliveryPerson}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssignDialogOpen(false);
      setMessage({ type: 'success', text: 'Delivery person assigned successfully' });
      fetchOrders();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to assign delivery' });
    } finally {
      setAssigning(false);
    }
  };

  // Group B — Add Delivery wizard
  const openAddDeliveryWizard = async (order) => {
    setAddDeliveryOrder(order);
    setAddDeliveryStep(1);
    setAddDeliveryData({
      zone_id: '', delivery_charge: '', is_complementary: false,
      receiver_name: order?.customer_info?.name || '',
      receiver_phone: order?.customer_info?.phone || '',
      delivery_address: order?.delivery_address || '',
      delivery_city: order?.delivery_city || '',
      assign_delivery_person_id: ''
    });
    setAddDeliveryOpen(true);
    try {
      const [zRes, cRes, dRes] = await Promise.all([
        axios.get(`${API_URL}/api/zones?outlet_id=${order.outlet_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/cities`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/delivery/persons`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAddDeliveryZones(zRes.data || []);
      setAddDeliveryCities(cRes.data || []);
      setDeliveryPersons(dRes.data || []);
    } catch (err) {
      console.error('Failed to load wizard data', err);
    }
  };

  const submitAddDelivery = async () => {
    if (!addDeliveryOrder) return;
    // Validate
    if (!addDeliveryData.zone_id) return setMessage({ type: 'error', text: 'Please select a zone' });
    if (!addDeliveryData.is_complementary && addDeliveryData.zone_id === 'custom' && !addDeliveryData.delivery_charge)
      return setMessage({ type: 'error', text: 'Please enter delivery charge' });
    if (!addDeliveryData.delivery_address) return setMessage({ type: 'error', text: 'Please enter delivery address' });
    if (!addDeliveryData.delivery_city) return setMessage({ type: 'error', text: 'Please select a city' });
    if (!addDeliveryData.receiver_phone) return setMessage({ type: 'error', text: 'Please enter receiver phone' });

    setAddingDelivery(true);
    try {
      const payload = {
        zone_id: addDeliveryData.zone_id,
        is_complementary: !!addDeliveryData.is_complementary,
        delivery_charge: addDeliveryData.is_complementary ? 0 : parseFloat(addDeliveryData.delivery_charge || 0),
        delivery_address: addDeliveryData.delivery_address,
        delivery_city: addDeliveryData.delivery_city,
        receiver_info: {
          name: addDeliveryData.receiver_name,
          phone: addDeliveryData.receiver_phone,
          address: addDeliveryData.delivery_address,
          city: addDeliveryData.delivery_city,
        },
        ...(addDeliveryData.assign_delivery_person_id ? { assign_delivery_person_id: addDeliveryData.assign_delivery_person_id } : {})
      };
      await axios.post(
        `${API_URL}/api/orders/${addDeliveryOrder.id}/add-delivery`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddDeliveryOpen(false);
      setMessage({ type: 'success', text: 'Delivery added to order' });
      fetchOrders();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add delivery' });
    } finally {
      setAddingDelivery(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...orders];

    // Tab filter
    if (activeTab !== 'all') {
      filtered = filtered.filter(order => order.status === activeTab);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(order => order.delivery_date >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(order => order.delivery_date <= dateTo);
    }

    // Occasion filter
    if (occasionFilter !== 'all') {
      filtered = filtered.filter(order => order.occasion === occasionFilter);
    }

    // Flavour filter
    if (flavourFilter !== 'all') {
      filtered = filtered.filter(order => order.flavour === flavourFilter);
    }

    // Outlet filter
    if (outletFilter !== 'all') {
      filtered = filtered.filter(order => order.outlet_id === outletFilter);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(term) ||
        order.customer_info?.name?.toLowerCase().includes(term) ||
        order.customer_info?.phone?.toLowerCase().includes(term)
      );
    }

    // Sort by delivery time
    filtered.sort((a, b) => {
      const dateA = a.delivery_date || '';
      const dateB = b.delivery_date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = a.delivery_time || '';
      const timeB = b.delivery_time || '';
      return timeA.localeCompare(timeB);
    });

    setFilteredOrders(filtered);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      // Validation: Cannot assign to delivery (picked_up) without photo upload
      if (newStatus === 'picked_up') {
        const order = orders.find(o => o.id === orderId);
        if (!order.actual_cake_image_url) {
          setMessage({ 
            type: 'error', 
            text: 'Cannot assign to delivery! Counter person must upload actual cake photo first.' 
          });
          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
          return;
        }
      }

      await axios.patch(
        `${API_URL}/api/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: `Order status updated to ${STATUS_CONFIG[newStatus].label}` });
      fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage({ type: 'error', text: 'Failed to update order status' });
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };
  
  const handleEditOrder = (order) => {
    setEditFormData({
      ...order,
      customer_name: order.customer_info?.name || '',
      customer_phone: order.customer_info?.phone || '',
      customer_address: order.customer_info?.address || order.delivery_address || ''
    });
    setEditDialogOpen(true);
  };
  
  const submitEditOrder = async () => {
    try {
      const updateData = {
        customer_info: {
          name: editFormData.customer_name,
          phone: editFormData.customer_phone,
          address: editFormData.customer_address
        },
        delivery_date: editFormData.delivery_date,
        delivery_time: editFormData.delivery_time,
        occasion: editFormData.occasion,
        flavour: editFormData.flavour,
        size_pounds: parseFloat(editFormData.size_pounds),
        name_on_cake: editFormData.name_on_cake,
        total_amount: parseFloat(editFormData.total_amount),
        special_instructions: editFormData.special_instructions,
        cake_image_url: editFormData.cake_image_url,
      };
      // Super admin can set/edit base_size — appears only on the kitchen view + KOT.
      const bs = editFormData.base_size;
      if (bs !== '' && bs !== null && bs !== undefined && !Number.isNaN(parseFloat(bs))) {
        updateData.base_size = parseFloat(bs);
      } else if (bs === '' || bs === null) {
        updateData.base_size = null;
      }
      
      await axios.patch(
        `${API_URL}/api/orders/${editFormData.id}`,
        {
          ...updateData,
          secondary_images: editFormData.secondary_images || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: 'Order updated successfully' });
      setEditDialogOpen(false);
      setEditFormData(null);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update order' });
    }
  };

  const openDiscountDialog = (order) => {
    setDiscountOrder(order);
    setDiscountAmount('');
    setDiscountReason('');
    setDiscountDialogOpen(true);
  };

  const submitDiscount = async () => {
    if (!discountOrder) return;
    const amt = parseFloat(discountAmount);
    if (!amt || amt <= 0) {
      setMessage({ type: 'error', text: 'Enter a discount amount greater than 0' });
      return;
    }
    if (amt > (discountOrder.pending_amount || 0)) {
      setMessage({ type: 'error', text: 'Discount cannot exceed pending amount' });
      return;
    }
    try {
      setDiscountSubmitting(true);
      const res = await axios.post(
        `${API_URL}/api/orders/${discountOrder.id}/apply-discount`,
        { discount_amount: amt, reason: discountReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({
        type: 'success',
        text: `Discount of ₹${amt} applied. New total: ₹${res.data.total_amount}, pending: ₹${res.data.pending_amount}`
      });
      setDiscountDialogOpen(false);
      setDiscountOrder(null);
      fetchOrders();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to apply discount' });
    } finally {
      setDiscountSubmitting(false);
    }
  };
  
  const handleDeleteOrder = (order) => {
    setDeleteOrder(order);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrder = async () => {
    if (!deleteOrder) return;
    if (!deleteReason.trim()) {
      setMessage({ type: 'error', text: 'A reason is required to delete an order.' });
      return;
    }

    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/orders/${deleteOrder.id}?reason=${encodeURIComponent(deleteReason.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: res.data?.message || 'Done' });
      setDeleteDialogOpen(false);
      setDeleteOrder(null);
      setDeleteReason('');
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete order' });
    } finally {
      setDeleting(false);
    }
  };

  const openPhotoUploadModal = (order) => {
    if (!order.is_ready) {
      setMessage({ type: 'error', text: 'Order must be marked as ready by kitchen first' });
      return;
    }
    if (order.actual_cake_image_url) {
      setMessage({ type: 'error', text: 'Photo already uploaded for this order' });
      return;
    }
    setSelectedOrderForPhoto(order);
    setPhotoUploadModalOpen(true);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) {
      setMessage({ type: 'error', text: 'Please select a photo' });
      return;
    }

    try {
      // Step 1: Upload image file
      const formData = new FormData();
      formData.append('file', photoFile);

      const uploadResponse = await axios.post(`${API_URL}/api/upload-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = uploadResponse.data.url;

      // Step 2: Use specialized endpoint to save actual photo and trigger incentive
      await axios.post(
        `${API_URL}/api/orders/${selectedOrderForPhoto.id}/upload-actual-photo?image_url=${encodeURIComponent(imageUrl)}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setPhotoUploadModalOpen(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedOrderForPhoto(null);
      setMessage({ type: 'success', text: 'Photo uploaded successfully! Incentive calculated for sales person.' });
      
      fetchOrders();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Failed to upload photo:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to upload photo' });
    }
  };

  // ==================== CAMERA CAPTURE FOR READY TO DELIVER ====================
  const openCameraForDelivery = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    setCameraOrderId(orderId);
    setCameraOrder(order);
    setCapturedPhoto(null);
    setDeliveryChoiceStep(false);
    setCameraDialogOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      setCameraStream(stream);
      setTimeout(() => {
        const video = document.getElementById('camera-preview');
        if (video) video.srcObject = stream;
      }, 100);
    } catch (err) {
      console.error('Camera access failed:', err);
      setMessage({ type: 'error', text: 'Camera access denied. Please allow camera permission.' });
      setCameraDialogOpen(false);
    }
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-preview');
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      setCapturedPhoto({ blob, url });
    }, 'image/jpeg', 0.85);
    stopCamera();
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const retakePhoto = async () => {
    setCapturedPhoto(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      setCameraStream(stream);
      setTimeout(() => {
        const video = document.getElementById('camera-preview');
        if (video) video.srcObject = stream;
      }, 100);
    } catch (err) {
      console.error('Camera restart failed:', err);
    }
  };

  const closeCameraDialog = () => {
    stopCamera();
    setCameraDialogOpen(false);
    setCameraOrderId(null);
    setCameraOrder(null);
    setCapturedPhoto(null);
    setDeliveryChoiceStep(false);
    setTransferOutletStep(false);
    setSelectedTransferOutlet('');
  };

  // Upload photo + transfer to specific outlet (optionally customer pickup)
  const uploadCapturedAndTransferOutlet = async (pickupByCustomer) => {
    if (!capturedPhoto?.blob || !cameraOrderId || !selectedTransferOutlet) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', capturedPhoto.blob, 'cake-photo.jpg');
      const uploadRes = await axios.post(`${API_URL}/api/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      const imageUrl = uploadRes.data.url;
      await axios.post(
        `${API_URL}/api/orders/${cameraOrderId}/ready-to-deliver?image_url=${encodeURIComponent(imageUrl)}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      await axios.post(
        `${API_URL}/api/orders/${cameraOrderId}/transfer-outlet?outlet_id=${encodeURIComponent(selectedTransferOutlet)}&pickup_by_customer=${pickupByCustomer}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      closeCameraDialog();
      setMessage({
        type: 'success',
        text: pickupByCustomer
          ? 'Photo uploaded! Order set for customer pickup at selected outlet.'
          : 'Photo uploaded! Order transferred to selected outlet.'
      });
      fetchOrders();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Upload/transfer failed:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to transfer order' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const uploadCapturedAndMarkDelivery = async (isPickup = false) => {
    if (!capturedPhoto?.blob || !cameraOrderId) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', capturedPhoto.blob, 'cake-photo.jpg');
      const uploadRes = await axios.post(`${API_URL}/api/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      const imageUrl = uploadRes.data.url;
      await axios.post(
        `${API_URL}/api/orders/${cameraOrderId}/ready-to-deliver?image_url=${encodeURIComponent(imageUrl)}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // If customer pickup, mark order as pickup (removes from delivery dashboard)
      if (isPickup) {
        await axios.post(
          `${API_URL}/api/orders/${cameraOrderId}/set-pickup?pickup=true`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      closeCameraDialog();
      setMessage({ 
        type: 'success', 
        text: isPickup 
          ? 'Photo uploaded! Order marked for Customer Pickup. Incentive calculated.' 
          : 'Order marked as Ready to Deliver! Incentive calculated.' 
      });
      fetchOrders();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Upload failed:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to upload photo' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAddPayment = (order) => {
    setSelectedOrder(order);
    setPaymentForm({
      amount: Math.max(0, order.total_amount - order.paid_amount).toString(),
      payment_method: 'cash'
    });
    setPaymentDialogOpen(true);
  };

  const submitPayment = async () => {
    try {
      await axios.post(
        `${API_URL}/api/payments`,
        {
          order_id: selectedOrder.id,
          amount: parseFloat(paymentForm.amount),
          payment_method: paymentForm.payment_method
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: 'Payment recorded successfully' });
      setPaymentDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error('Error recording payment:', error);
      setMessage({ type: 'error', text: 'Failed to record payment' });
    }
  };

  const handleTransferOrder = (order) => {
    setSelectedOrder(order);
    setTransferDialogOpen(true);
  };

  const submitTransfer = async (newOutletId) => {
    try {
      await axios.post(
        `${API_URL}/api/orders/${selectedOrder.id}/transfer?new_outlet_id=${newOutletId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: 'Order transferred successfully!' });
      setTransferDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error('Error transferring order:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to transfer order' });
    }
  };

  const handleCancelDelivery = async (order) => {
    if (!window.confirm(`Cancel delivery for order ${order.order_number}? Delivery charges will be removed.`)) {
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/orders/${order.id}/cancel-delivery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: 'Delivery cancelled and charges removed' });
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling delivery:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to cancel delivery' });
    }
  };

  const downloadOrdersPDF = () => {
    if (!filteredOrders || filteredOrders.length === 0) {
      alert('No orders to download');
      return;
    }
    
    const dateRange = dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : 'All dates';
    
    const rows = filteredOrders.map(order => `
      <tr>
        <td>${order.order_number}</td>
        <td>${order.customer_info?.name || 'N/A'}<br/><small>${order.customer_info?.phone || ''}</small></td>
        <td>${order.flavour || 'N/A'}</td>
        <td>${order.size_pounds || '-'} lbs</td>
        <td>${order.delivery_date || ''}<br/><small>${formatTime12(order.delivery_time)}</small></td>
        <td>${order.occasion || 'N/A'}</td>
        <td>${order.status?.replace(/_/g, ' ').toUpperCase() || ''}</td>
        <td>₹${order.total_amount?.toFixed(2) || '0'}</td>
        <td>₹${order.paid_amount?.toFixed(2) || '0'}</td>
        <td>₹${order.pending_amount?.toFixed(2) || '0'}</td>
        <td>${order.needs_delivery ? 'Delivery' : 'Pickup'}</td>
      </tr>
    `).join('');
    
    const totalAmount = filteredOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
    const totalPaid = filteredOrders.reduce((sum, o) => sum + (o.paid_amount || 0), 0);
    const totalPending = filteredOrders.reduce((sum, o) => sum + (o.pending_amount || 0), 0);
    
    const html = `
    <html>
    <head>
      <title>US Bakers - Orders Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
        h1 { color: #e92587; text-align: center; }
        h3 { text-align: center; color: #666; }
        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        th { background: #e92587; color: white; padding: 6px 4px; text-align: left; font-size: 10px; }
        td { padding: 5px 4px; border-bottom: 1px solid #eee; font-size: 10px; }
        tr:hover { background: #fff5f9; }
        .summary { margin-top: 20px; display: flex; justify-content: space-around; }
        .summary-card { background: #f8f8f8; padding: 10px 20px; border-radius: 8px; text-align: center; }
        .summary-card .amount { font-size: 18px; font-weight: bold; color: #e92587; }
        small { color: #888; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <h1>US BAKERS</h1>
      <h3>Orders Report - ${dateRange} (${filteredOrders.length} orders)</h3>
      <div class="summary">
        <div class="summary-card"><div>Total Amount</div><div class="amount">₹${totalAmount.toFixed(2)}</div></div>
        <div class="summary-card"><div>Total Paid</div><div class="amount" style="color: green;">₹${totalPaid.toFixed(2)}</div></div>
        <div class="summary-card"><div>Total Pending</div><div class="amount" style="color: orange;">₹${totalPending.toFixed(2)}</div></div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Order #</th><th>Customer</th><th>Flavour</th><th>Size</th><th>Delivery</th><th>Occasion</th><th>Status</th><th>Total</th><th>Paid</th><th>Pending</th><th>Type</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="text-align:center; color:#999; margin-top:20px;">Generated on ${new Date().toLocaleString('en-IN', { hour12: true })}</p>
    </body>
    </html>`;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };



  const exportOrdersToExcel = () => {
    if (!filteredOrders || filteredOrders.length === 0) {
      alert('No orders to export');
      return;
    }

    const outletName = (id) => (outlets.find(o => o.id === id)?.name) || '';
    const fmtDate = (v) => {
      if (!v) return '';
      try { return new Date(v).toLocaleString('en-IN'); } catch { return String(v); }
    };
    const yn = (b) => (b ? 'Yes' : 'No');

    const ws_data = [
      ['Manage Orders Export'],
      ['Generated', new Date().toLocaleString('en-IN')],
      ['Filters', JSON.stringify({
        outlet: outletFilter, activeTab, dateFrom, dateTo,
        statusFilter, occasion: occasionFilter, flavour: flavourFilter,
        search: searchTerm,
      })],
      [],
      [
        'Order #', 'Booked On', 'Outlet', 'Order Taken By',
        'Order Type', 'Customer Name', 'Customer Phone', 'Alt Phone',
        'Birthday', 'Gender',
        'Receiver Name', 'Receiver Phone', 'Receiver Address',
        'Needs Delivery', 'Delivery Address', 'Delivery City', 'Zone',
        'Occasion', 'Flavour', 'Size (lbs)', 'Base Size (lbs)', 'Name on Cake',
        'Special Instructions',
        'Delivery Date', 'Delivery Time',
        'Status', 'Lifecycle', 'Is Punch', 'Is Hold', 'Is Ready',
        'Is Credit', 'Is Complementary', 'Pickup by Customer',
        'Total (₹)', 'Paid (₹)', 'Pending (₹)', 'Delivery Charge (₹)', 'Discount (₹)',
        'PetPooja Bill Nos', 'PetPooja Comment', 'Modified After Ready', 'Modification Count',
        'Assigned Delivery Partner', 'Picked Up At', 'Reached At', 'Delivered At',
        'Ready At', 'Photo Uploaded At',
        'WhatsApp Alerts',
        'Created At', 'Updated At',
      ],
    ];

    filteredOrders.forEach(order => {
      const ci = order.customer_info || {};
      const ri = order.receiver_info || {};
      ws_data.push([
        order.order_number || '',
        fmtDate(order.created_at),
        outletName(order.outlet_id),
        order.order_taken_by_name || order.order_taken_by || '',
        order.order_type || '',
        ci.name || '',
        ci.phone || '',
        ci.alternate_phone || '',
        ci.birthday || '',
        ci.gender || '',
        ri.name || '',
        ri.phone || '',
        ri.address || '',
        yn(order.needs_delivery),
        order.delivery_address || '',
        order.delivery_city || '',
        order.zone_id || '',
        order.occasion || '',
        order.flavour || '',
        order.size_pounds ?? '',
        order.base_size ?? '',
        order.name_on_cake || '',
        (order.special_instructions || '').replace(/\r?\n/g, ' | '),
        order.delivery_date || '',
        order.delivery_time || '',
        order.status || '',
        order.lifecycle_status || '',
        yn(order.is_punch_order),
        yn(order.is_hold),
        yn(order.is_ready),
        yn(order.is_credit_order),
        yn(order.is_complementary),
        yn(order.pickup_by_customer),
        order.total_amount ?? 0,
        order.paid_amount ?? 0,
        order.pending_amount ?? 0,
        order.delivery_charge ?? 0,
        order.discount_amount ?? 0,
        (order.petpooja_bill_numbers || []).join(', '),
        order.petpooja_comment || '',
        yn(order.modified_after_ready),
        order.modification_count ?? 0,
        order.assigned_delivery_partner_name || order.assigned_delivery_partner || '',
        fmtDate(order.picked_up_at),
        fmtDate(order.reached_at),
        fmtDate(order.delivered_at),
        fmtDate(order.ready_at),
        fmtDate(order.photo_uploaded_at),
        yn(order.whatsapp_alerts),
        fmtDate(order.created_at),
        fmtDate(order.updated_at),
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, `orders_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handlePrintKOT = (order) => {
    const outlet = outlets.find(o => o.id === order.outlet_id);
    const outletName = outlet ? outlet.name : 'N/A';
    const takenByName = order.order_taken_by_name || 'Unknown';
    const bufferMin = Number(outlet?.ready_time_buffer_minutes ?? 0);
    const kotTime = subtractMinutesFromTime(order.delivery_time, bufferMin);
    const kotTime12 = to12Hour(kotTime);
    const deliveryTime12 = to12Hour(order.delivery_time);
    const cakeAmount = Math.max(0, (order.total_amount || 0) - (order.delivery_charge || 0));
    const isComplementaryDelivery = order.is_complementary || (order.delivery_charge === 0 && order.needs_delivery && order.is_complementary);
    const balance = (order.pending_amount || 0);

    const printWindow = window.open('', '_blank');

    const instructions = order.special_instructions
      ? order.special_instructions.split('\n').filter(l => l.trim()).map(l => `* ${l.trim()}`).join('\n')
      : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>KOT - ${order.order_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; font-weight: bold !important; }
            body {
              font-family: 'Courier New', 'Lucida Console', monospace;
              font-size: 13px;
              line-height: 1.45;
              width: 80mm;
              padding: 4mm;
              color: #000;
              font-weight: bold;
            }
            .center { text-align: center; }
            .divider {
              border: none;
              border-top: 1px dashed #000;
              margin: 4px 0;
            }
            .double-divider {
              border: none;
              border-top: 2px solid #000;
              margin: 4px 0;
            }
            .row {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
            }
            .big { font-size: 17px; }
            .instructions {
              border: 1.5px solid #000;
              padding: 5px;
              margin: 4px 0;
              white-space: pre-line;
              font-size: 13px;
            }
            .footer { font-size: 11px; text-align: center; margin-top: 6px; }
            @media print {
              @page {
                size: 80mm auto;
                margin: 0;
              }
              body { width: 80mm; }
            }
          </style>
        </head>
        <body>
          <div class="center big">US BAKERS</div>
          <div class="center" style="font-size:11px;">Kitchen Order Ticket</div>
          <hr class="double-divider" />

          <div class="row">
            <span class="big">Order #${order.order_number}</span>
          </div>
          <div class="row">
            <span>Date:</span>
            <span>${new Date(order.created_at).toLocaleDateString('en-IN')}</span>
          </div>
          <div class="row">
            <span>Outlet:</span>
            <span>${outletName}</span>
          </div>
          <div class="row">
            <span>Taken By:</span>
            <span>${takenByName}</span>
          </div>
          <hr class="divider" />

          <div class="row">
            <span>Delivery:</span>
            <span>${order.delivery_date}</span>
          </div>
          <div class="row">
            <span>Time:</span>
            <span>${deliveryTime12 || 'N/A'}</span>
          </div>
          ${kotTime && kotTime !== order.delivery_time ? `
          <div class="row" style="font-size:14px; border:1.5px solid #000; padding:3px; margin:3px 0;">
            <span>READY BY:</span>
            <span>${kotTime12}</span>
          </div>` : ''}
          <div class="row">
            <span>Delivery Status:</span>
            <span>${order.needs_delivery ? 'YES' : 'NO (Pickup)'}</span>
          </div>
          <div class="row">
            <span>Status:</span>
            <span>${(order.status || '').replace(/_/g, ' ').toUpperCase()}</span>
          </div>
          <hr class="divider" />

          <div style="font-size:12px;">CUSTOMER</div>
          <div class="row">
            <span>${order.customer_info?.name || 'N/A'}</span>
          </div>
          <div class="row">
            <span>Ph: ${order.customer_info?.phone || 'N/A'}</span>
          </div>
          ${order.delivery_address ? `<div style="font-size:11px;">Addr: ${order.delivery_address}</div>` : ''}
          <hr class="divider" />

          <div style="font-size:12px;">CAKE DETAILS</div>
          <div class="row">
            <span>Flavour:</span>
            <span>${order.flavour || 'N/A'}</span>
          </div>
          <div class="row">
            <span>Size:</span>
            <span>${order.size_pounds} Pounds</span>
          </div>
          ${order.base_size ? `
          <div class="row">
            <span>Base Size:</span>
            <span>${order.base_size} Pounds</span>
          </div>` : ''}
          <div class="row">
            <span>Occasion:</span>
            <span>${order.occasion || 'N/A'}</span>
          </div>
          <div class="row">
            <span>Name on Cake:</span>
            <span>${order.name_on_cake ? '"' + order.name_on_cake + '"' : 'N/A'}</span>
          </div>

          ${instructions ? `
          <hr class="divider" />
          <div class="instructions">INSTRUCTIONS:\n${instructions}</div>
          ` : ''}

          <hr class="divider" />
          <div style="font-size:12px;">PAYMENT</div>
          <div class="row">
            <span>Cake Amount:</span>
            <span>Rs.${cakeAmount.toFixed(2)}</span>
          </div>
          <div class="row">
            <span>Delivery Amount:</span>
            <span>${isComplementaryDelivery ? 'COMPLEMENTARY' : 'Rs.' + (order.delivery_charge || 0).toFixed(2)}</span>
          </div>
          ${order.discount_amount && order.discount_amount > 0 ? `
          <div class="row">
            <span>Discount:</span>
            <span>- Rs.${order.discount_amount.toFixed(2)}</span>
          </div>` : ''}
          <div class="row" style="border-top:1px dashed #000; padding-top:2px;">
            <span>Total Amount:</span>
            <span>Rs.${(order.total_amount || 0).toFixed(2)}</span>
          </div>
          <div class="row">
            <span>Paid Amount:</span>
            <span>Rs.${(order.paid_amount || 0).toFixed(2)}</span>
          </div>
          <div class="row">
            <span>Balance Amount:</span>
            <span>${isComplementaryDelivery && balance <= 0 ? 'COMPLEMENTARY' : 'Rs.' + balance.toFixed(2)}</span>
          </div>

          ${order.petpooja_bill_numbers && order.petpooja_bill_numbers.length > 0 ? `
          <hr class="divider" />
          <div style="font-size:12px;">PETPOOJA BILLING</div>
          <div class="row">
            <span>Outlet:</span>
            <span>${outletName}</span>
          </div>
          <div class="row">
            <span>Bill No:</span>
            <span>${order.petpooja_bill_numbers.join(', ')}</span>
          </div>
          <div class="center" style="margin-top:4px; font-size:12px; border:1.5px solid #000; padding:3px;">
            BILLING DONE IN PETPOOJA
          </div>
          ` : ''}

          <hr class="double-divider" />
          <div class="footer">
            ${new Date().toLocaleString('en-IN')}<br/>
            Thank You!
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleBulkPrintKOT = () => {
    const ordersToPrint = orders.filter(o => selectedOrderIds.includes(o.id));
    if (ordersToPrint.length === 0) return;
    const printWindow = window.open('', '_blank');
    let html = `<!DOCTYPE html><html><head><title>Bulk KOT</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; font-weight: bold !important; }
        body { font-family: 'Courier New', 'Lucida Console', monospace; font-size: 13px; line-height: 1.45; color: #000; font-weight: bold; }
        .kot-page { width: 80mm; padding: 4mm; page-break-after: always; }
        .kot-page:last-child { page-break-after: auto; }
        .center { text-align: center; }
        .big { font-size: 17px; }
        .divider { border: none; border-top: 1px dashed #000; margin: 4px 0; }
        .double-divider { border: none; border-top: 2px solid #000; margin: 4px 0; }
        .row { display: flex; justify-content: space-between; margin: 2px 0; }
        .instructions { border: 1.5px solid #000; padding: 5px; margin: 4px 0; white-space: pre-line; font-size: 13px; }
        .footer { font-size: 11px; text-align: center; margin-top: 6px; }
        @media print { @page { size: 80mm auto; margin: 0; } }
      </style></head><body>`;
    ordersToPrint.forEach(order => {
      const outlet = outlets.find(o => o.id === order.outlet_id);
      const outletName = outlet ? outlet.name : 'N/A';
      const takenByName = order.order_taken_by_name || 'Unknown';
      const bufferMin = Number(outlet?.ready_time_buffer_minutes ?? 0);
      const kotTime = subtractMinutesFromTime(order.delivery_time, bufferMin);
      const kotTime12 = to12Hour(kotTime);
      const deliveryTime12 = to12Hour(order.delivery_time);
      const cakeAmount = Math.max(0, (order.total_amount || 0) - (order.delivery_charge || 0));
      const isCompDelivery = order.is_complementary || (order.delivery_charge === 0 && order.needs_delivery && order.is_complementary);
      const balance = (order.pending_amount || 0);
      const instructions = order.special_instructions
        ? order.special_instructions.split('\n').filter(l => l.trim()).map(l => `* ${l.trim()}`).join('\n')
        : '';
      html += `<div class="kot-page">
        <div class="center big">US BAKERS</div>
        <div class="center" style="font-size:11px;">Kitchen Order Ticket</div>
        <hr class="double-divider" />
        <div class="row"><span class="big">Order #${order.order_number}</span></div>
        <div class="row"><span>Date:</span><span>${new Date(order.created_at).toLocaleDateString('en-IN')}</span></div>
        <div class="row"><span>Outlet:</span><span>${outletName}</span></div>
        <div class="row"><span>Taken By:</span><span>${takenByName}</span></div>
        <hr class="divider" />
        <div class="row"><span>Delivery:</span><span>${order.delivery_date}</span></div>
        <div class="row"><span>Time:</span><span>${deliveryTime12 || 'N/A'}</span></div>
        ${kotTime && kotTime !== order.delivery_time ? `<div class="row" style="font-size:14px; border:1.5px solid #000; padding:3px; margin:3px 0;"><span>READY BY:</span><span>${kotTime12}</span></div>` : ''}
        <div class="row"><span>Delivery Status:</span><span>${order.needs_delivery ? 'YES' : 'NO (Pickup)'}</span></div>
        <div class="row"><span>Status:</span><span>${(order.status || '').replace(/_/g, ' ').toUpperCase()}</span></div>
        <hr class="divider" />
        <div style="font-size:12px;">CUSTOMER</div>
        <div>${order.customer_info?.name || 'N/A'}</div>
        <div>Ph: ${order.customer_info?.phone || 'N/A'}</div>
        ${order.delivery_address ? `<div style="font-size:11px;">Addr: ${order.delivery_address}</div>` : ''}
        <hr class="divider" />
        <div style="font-size:12px;">CAKE DETAILS</div>
        <div class="row"><span>Flavour:</span><span>${order.flavour || 'N/A'}</span></div>
        <div class="row"><span>Size:</span><span>${order.size_pounds} Pounds</span></div>
        ${order.base_size ? `<div class="row"><span>Base Size:</span><span>${order.base_size} Pounds</span></div>` : ''}
        <div class="row"><span>Occasion:</span><span>${order.occasion || 'N/A'}</span></div>
        <div class="row"><span>Name on Cake:</span><span>${order.name_on_cake ? '"' + order.name_on_cake + '"' : 'N/A'}</span></div>
        ${instructions ? `<hr class="divider" /><div class="instructions">INSTRUCTIONS:\n${instructions}</div>` : ''}
        <hr class="divider" />
        <div style="font-size:12px;">PAYMENT</div>
        <div class="row"><span>Cake Amount:</span><span>Rs.${cakeAmount.toFixed(2)}</span></div>
        <div class="row"><span>Delivery Amount:</span><span>${isCompDelivery ? 'COMPLEMENTARY' : 'Rs.' + (order.delivery_charge || 0).toFixed(2)}</span></div>
        ${order.discount_amount && order.discount_amount > 0 ? `<div class="row"><span>Discount:</span><span>- Rs.${order.discount_amount.toFixed(2)}</span></div>` : ''}
        <div class="row" style="border-top:1px dashed #000; padding-top:2px;"><span>Total Amount:</span><span>Rs.${(order.total_amount || 0).toFixed(2)}</span></div>
        <div class="row"><span>Paid Amount:</span><span>Rs.${(order.paid_amount || 0).toFixed(2)}</span></div>
        <div class="row"><span>Balance Amount:</span><span>${isCompDelivery && balance <= 0 ? 'COMPLEMENTARY' : 'Rs.' + balance.toFixed(2)}</span></div>
        ${order.petpooja_bill_numbers && order.petpooja_bill_numbers.length > 0 ? `
        <hr class="divider" />
        <div style="font-size:12px;">PETPOOJA BILLING</div>
        <div class="row"><span>Outlet:</span><span>${outletName}</span></div>
        <div class="row"><span>Bill No:</span><span>${order.petpooja_bill_numbers.join(', ')}</span></div>
        <div class="center" style="margin-top:4px; font-size:12px; border:1.5px solid #000; padding:3px;">BILLING DONE IN PETPOOJA</div>
        ` : ''}
        <hr class="double-divider" />
        <div class="footer">${new Date().toLocaleString('en-IN')}<br/>Thank You!</div>
      </div>`;
    });
    html += `</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
    setSelectedOrderIds([]);
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => 
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    const currentPageOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const allSelected = currentPageOrders.every(o => selectedOrderIds.includes(o.id));
    if (allSelected) {
      setSelectedOrderIds(prev => prev.filter(id => !currentPageOrders.find(o => o.id === id)));
    } else {
      setSelectedOrderIds(prev => [...new Set([...prev, ...currentPageOrders.map(o => o.id)])]);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status];
    if (!config) return null;
    
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <LayoutWithSidebar>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading orders...</div>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#e92587' }}>Manage Orders</h1>
            <p className="text-gray-600 mt-1">Track and manage all bakery orders</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportOrdersToExcel} disabled={!filteredOrders || filteredOrders.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={downloadOrdersPDF} disabled={!filteredOrders || filteredOrders.length === 0} className="border-pink-600 text-pink-600 hover:bg-pink-50">
              <Printer className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {message.text && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="search">Search</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder="Order #, Customer name, Phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="date-from">From Date</Label>
                <Input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-1"
                  data-testid="manage-orders-date-from"
                />
              </div>

              <div>
                <Label htmlFor="date-to">To Date</Label>
                <Input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-1"
                  data-testid="manage-orders-date-to"
                />
              </div>

              <div className="md:col-span-2 flex items-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const t = localToday();
                    setDateFrom(t);
                    setDateTo(t);
                  }}
                  data-testid="manage-orders-quick-today"
                >
                  Today
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 1);
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    const t = `${y}-${m}-${day}`;
                    setDateFrom(t);
                    setDateTo(t);
                  }}
                  data-testid="manage-orders-quick-tomorrow"
                >
                  Tomorrow
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  data-testid="manage-orders-clear-dates"
                >
                  Clear Dates
                </Button>
              </div>

              <div>
                <Label htmlFor="status-filter">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="occasion-filter">Occasion</Label>
                <Select value={occasionFilter} onValueChange={setOccasionFilter}>
                  <SelectTrigger className="mt-1" data-testid="manage-orders-occasion-filter">
                    <SelectValue placeholder="All Occasions" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All Occasions</SelectItem>
                    {occasionsList.map((occ) => (
                      <SelectItem key={occ.id || occ.name} value={occ.name}>{occ.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="flavour-filter">Flavour</Label>
                <Select value={flavourFilter} onValueChange={setFlavourFilter}>
                  <SelectTrigger className="mt-1" data-testid="manage-orders-flavour-filter">
                    <SelectValue placeholder="All Flavours" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All Flavours</SelectItem>
                    {flavoursList.map((flav) => (
                      <SelectItem key={flav.id || flav.name} value={flav.name}>{flav.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="outlet-filter">Outlet</Label>
                <Select value={outletFilter} onValueChange={setOutletFilter}>
                  <SelectTrigger className="mt-1" data-testid="manage-orders-outlet-filter">
                    <SelectValue placeholder="All Outlets" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Outlets</SelectItem>
                    {outlets.map(outlet => (
                      <SelectItem key={outlet.id} value={outlet.id} data-testid={`outlet-option-${outlet.id}`}>
                        {outlet.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setActiveTab('all');
                    setDateFrom('');
                    setDateTo('');
                    setOccasionFilter('all');
                    setFlavourFilter('all');
                    setOutletFilter('all');
                  }}
                  className="w-full"
                  data-testid="manage-orders-clear-all-filters"
                >
                  Clear All Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs — counts respect Outlet + Date filters (not status tab itself) */}
        {(() => {
          const scoped = orders.filter(o => {
            if (outletFilter !== 'all' && o.outlet_id !== outletFilter) return false;
            if (dateFrom && (o.delivery_date || '') < dateFrom) return false;
            if (dateTo && (o.delivery_date || '') > dateTo) return false;
            return true;
          });
          const cnt = (s) => scoped.filter(o => o.status === s).length;
          return (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="all" data-testid="tab-all">All ({scoped.length})</TabsTrigger>
            <TabsTrigger value="confirmed" data-testid="tab-confirmed">
              Confirmed ({cnt('confirmed')})
            </TabsTrigger>
            <TabsTrigger value="in_progress" data-testid="tab-in-progress">
              Preparing ({cnt('in_progress')})
            </TabsTrigger>
            <TabsTrigger value="ready" data-testid="tab-ready">
              Ready ({cnt('ready')})
            </TabsTrigger>
            <TabsTrigger value="ready_to_deliver" data-testid="tab-ready-to-deliver">
              To Deliver ({cnt('ready_to_deliver')})
            </TabsTrigger>
            <TabsTrigger value="picked_up" data-testid="tab-picked-up">
              Out ({cnt('picked_up')})
            </TabsTrigger>
            <TabsTrigger value="delivered" data-testid="tab-delivered">
              Done ({cnt('delivered')})
            </TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled">
              Cancelled ({cnt('cancelled')})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Orders ({filteredOrders.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Bulk Actions Bar */}
                {selectedOrderIds.length > 0 && (
                  <div className="flex items-center gap-3 mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-700">{selectedOrderIds.length} selected</span>
                    <Button size="sm" variant="outline" onClick={handleBulkPrintKOT} data-testid="bulk-print-kot-btn">
                      <Printer className="h-3 w-3 mr-1" /> Print KOT
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedOrderIds([])}>Clear</Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]">
                          <input type="checkbox" className="rounded" onChange={toggleSelectAll}
                            checked={filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).length > 0 && 
                              filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).every(o => selectedOrderIds.includes(o.id))} />
                        </TableHead>
                        <TableHead>Order #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Cake Photo</TableHead>
                        <TableHead>Delivery</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOrders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                            No orders found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredOrders
                          .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                          .map((order) => (
                          <TableRow 
                            key={order.id}
                            className={`${order.status === 'delivered' ? 'bg-green-50 hover:bg-green-100' : ''} ${order.status === 'in_progress' ? 'bg-orange-50' : ''} ${order.status === 'ready' ? 'bg-green-50' : ''}`}
                          >
                            <TableCell>
                              <input type="checkbox" className="rounded"
                                checked={selectedOrderIds.includes(order.id)}
                                onChange={() => toggleOrderSelection(order.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center space-x-2">
                                <Package className="h-4 w-4 text-gray-400" />
                                <span>{order.order_number}</span>
                                {order.voice_instruction_url && (
                                  <Mic 
                                    className="h-3.5 w-3.5 text-blue-500 cursor-pointer" 
                                    title="Has voice instructions - click View to listen"
                                    onClick={() => handleViewOrder(order)}
                                  />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">{order.customer_info?.name}</div>
                                <div className="text-sm text-gray-500">{order.customer_info?.phone}</div>
                                {(() => {
                                  const o = outlets.find(x => x.id === order.outlet_id);
                                  return o ? (
                                    <div className="mt-1">
                                      <span
                                        className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-pink-50 text-pink-700 border border-pink-200"
                                        data-testid={`order-outlet-badge-${order.id}`}
                                        title={o.name}
                                      >
                                        {o.name}
                                      </span>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>
                              {order.cake_image_url ? (
                                <div className="flex items-center gap-1">
                                  <div 
                                    className="cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => {
                                      setPreviewImage(getImageUrl(order.cake_image_url));
                                      setImagePreviewOpen(true);
                                    }}
                                    title="Click to preview primary image"
                                  >
                                    <img
                                      src={getImageUrl(order.cake_image_url)}
                                      alt="Cake"
                                      className="w-12 h-12 object-cover rounded border-2 border-pink-200"
                                    />
                                  </div>
                                  {/* Show secondary images count */}
                                  {order.secondary_images && order.secondary_images.length > 0 && (
                                    <div 
                                      className="cursor-pointer hover:opacity-80 transition-opacity relative"
                                      onClick={() => {
                                        setPreviewImage(getImageUrl(order.secondary_images[0]));
                                        setSelectedOrder(order);
                                        setImagePreviewOpen(true);
                                      }}
                                      title={`+${order.secondary_images.length} more images`}
                                    >
                                      <img
                                        src={getImageUrl(order.secondary_images[0])}
                                        alt="Secondary"
                                        className="w-10 h-10 object-cover rounded border border-gray-200"
                                      />
                                      {order.secondary_images.length > 1 && (
                                        <span className="absolute -top-1 -right-1 bg-pink-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                          +{order.secondary_images.length - 1}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {/* Actual cake photo indicator */}
                                  {order.actual_cake_image_url && (
                                    <div 
                                      className="cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() => {
                                        setPreviewImage(getImageUrl(order.actual_cake_image_url));
                                        setImagePreviewOpen(true);
                                      }}
                                      title="Actual cake photo"
                                    >
                                      <img
                                        src={getImageUrl(order.actual_cake_image_url)}
                                        alt="Actual"
                                        className="w-10 h-10 object-cover rounded border-2 border-green-400"
                                      />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                  <ImageIcon className="h-5 w-5 text-gray-400" />
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-1 text-sm">
                                <Calendar className="h-3 w-3" />
                                <span>{order.delivery_date}</span>
                              </div>
                              <div className="text-sm text-gray-500">{formatTime12(order.delivery_time)}</div>
                              {order.created_at && (
                                <div className="text-[11px] text-gray-400 mt-1" data-testid={`booking-date-${order.id}`}>
                                  Booked: {new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{order.flavour} - {order.size_pounds} lbs</div>
                                {order.name_on_cake && (
                                  <div className="text-gray-500">"{order.name_on_cake}"</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div className="font-bold" style={{ color: '#e92587' }}>
                                  ₹{order.total_amount.toFixed(2)}
                                </div>
                                {order.delivery_charge > 0 && (
                                  <div className="text-xs text-gray-400">
                                    (incl. delivery ₹{order.delivery_charge.toFixed(2)})
                                  </div>
                                )}
                                {order.discount_amount > 0 && (
                                  <div className="text-xs text-emerald-700 font-medium" data-testid={`discount-badge-${order.id}`}>
                                    Discount: −₹{Number(order.discount_amount).toFixed(2)}
                                  </div>
                                )}
                                <div className="text-gray-500">
                                  Paid: ₹{order.paid_amount.toFixed(2)}
                                </div>
                                {order.pending_amount > 0 && (
                                  <div className="text-orange-600 font-semibold">
                                    Due: ₹{order.pending_amount.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(order.status)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end items-center space-x-2">
                                {/* Group B: Single "Upload Image" button — replaces the previous Upload + Ready-to-Deliver pair.
                                    Uses camera capture flow which also marks the order as ready_to_deliver. */}
                                {order.is_ready && !order.actual_cake_image_url && (
                                  <Button
                                    size="sm"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
                                    onClick={() => openCameraForDelivery(order.id)}
                                    data-testid={`upload-image-btn-${order.id}`}
                                    title="Capture or upload cake photo → auto-marks Ready to Deliver"
                                  >
                                    <Upload className="h-3 w-3 mr-1" />
                                    Upload Image
                                  </Button>
                                )}
                                {order.actual_cake_image_url && (
                                  <Badge className="bg-green-600 text-white text-xs">✓ Photo</Badge>
                                )}

                                {/* c6: Manual "Send cake photos to customer" (WhatsApp) */}
                                {order.actual_cake_image_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-pink-600 text-pink-700 hover:bg-pink-50 text-xs"
                                    onClick={() => openSendCakePhotosDialog(order)}
                                    data-testid={`send-cake-photos-btn-${order.id}`}
                                    title="Send reference + actual cake photo to the customer on WhatsApp"
                                  >
                                    <Send className="h-3 w-3 mr-1" />
                                    Send Photos
                                  </Button>
                                )}

                                {/* Assign Delivery Person Button */}
                                {order.status === 'ready_to_deliver' && !order.assigned_delivery_partner && order.needs_delivery && (
                                  <Button
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                                    onClick={() => openAssignDialog(order.id)}
                                    data-testid={`assign-delivery-btn-${order.id}`}
                                  >
                                    <Truck className="h-3 w-3 mr-1" />
                                    Assign Delivery
                                  </Button>
                                )}

                                {/* Group B: Add Delivery — for orders that don't have delivery yet */}
                                {order.status === 'ready_to_deliver' && !order.needs_delivery && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs border-orange-400 text-orange-700 hover:bg-orange-50"
                                    onClick={() => openAddDeliveryWizard(order)}
                                    data-testid={`add-delivery-btn-${order.id}`}
                                  >
                                    <Truck className="h-3 w-3 mr-1" />
                                    Add Delivery
                                  </Button>
                                )}

                                {/* Status Dropdown */}
                                <Select 
                                  value={order.status}
                                  onValueChange={(newStatus) => handleStatusUpdate(order.id, newStatus)}
                                >
                                  <SelectTrigger className="w-[140px] h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="confirmed">Confirmed</SelectItem>
                                    <SelectItem value="in_progress">Preparing</SelectItem>
                                    <SelectItem value="ready">Ready</SelectItem>
                                    <SelectItem value="ready_to_deliver" disabled={!order.actual_cake_image_url}>
                                      Ready to Deliver {!order.actual_cake_image_url && '(Photo Required)'}
                                    </SelectItem>
                                    <SelectItem 
                                      value="picked_up"
                                      disabled={!order.actual_cake_image_url}
                                    >
                                      Picked Up {!order.actual_cake_image_url && '🔒'}
                                    </SelectItem>
                                    <SelectItem value="delivered">Delivered</SelectItem>
                                  </SelectContent>
                                </Select>
                                
                                {/* More Menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="outline">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEditOrder(order)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit Order
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handlePrintKOT(order)}>
                                      <Printer className="h-4 w-4 mr-2" />
                                      Print KOT
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleTransferOrder(order)}>
                                      <ArrowRightLeft className="h-4 w-4 mr-2" />
                                      Transfer
                                    </DropdownMenuItem>
                                    {user?.role === 'super_admin' &&
                                      (order.status === 'ready' || order.status === 'ready_to_deliver') &&
                                      (order.pending_amount || 0) > 0 && (
                                      <DropdownMenuItem
                                        onClick={() => openDiscountDialog(order)}
                                        className="text-green-700"
                                        data-testid={`apply-discount-${order.id}`}
                                      >
                                        <BadgePercent className="h-4 w-4 mr-2" />
                                        Apply Discount
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteOrder(order)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination Controls */}
                {filteredOrders.length > itemsPerPage && (
                  <div className="flex items-center justify-between mt-4 px-4">
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.ceil(filteredOrders.length / itemsPerPage) }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            size="sm"
                            variant={currentPage === page ? "default" : "outline"}
                            onClick={() => setCurrentPage(page)}
                            className={currentPage === page ? "bg-pink-600 text-white" : ""}
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(Math.min(Math.ceil(filteredOrders.length / itemsPerPage), currentPage + 1))}
                        disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}

                {/* Status Legend */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                  <h4 className="font-semibold mb-3 text-sm text-gray-700">Status Legend</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <div key={key} className="flex items-center space-x-2">
                          <div className={`${config.color} w-8 h-8 rounded flex items-center justify-center`}>
                            {Icon && <Icon className="h-4 w-4 text-white" />}
                          </div>
                          <span className="text-sm">{config.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          );
        })()}

        {/* Image Preview Dialog */}
        <Dialog open={imagePreviewOpen} onOpenChange={setImagePreviewOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Order Images</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Main preview */}
              <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4 min-h-[400px]">
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="max-w-full max-h-[60vh] object-contain rounded"
                    onError={(e) => {
                      console.error('Image failed to load:', previewImage);
                      e.target.src = '/placeholder-image.png';
                    }}
                  />
                ) : (
                  <div className="text-gray-500">No image available</div>
                )}
              </div>
              {/* Thumbnail gallery for all images */}
              {selectedOrder && (
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  {selectedOrder.cake_image_url && (
                    <div 
                      className={`cursor-pointer rounded border-2 ${previewImage === getImageUrl(selectedOrder.cake_image_url) ? 'border-pink-500' : 'border-gray-200'}`}
                      onClick={() => setPreviewImage(getImageUrl(selectedOrder.cake_image_url))}
                    >
                      <img src={getImageUrl(selectedOrder.cake_image_url)} alt="Primary" className="w-16 h-16 object-cover rounded" />
                      <p className="text-xs text-center text-gray-500">Primary</p>
                    </div>
                  )}
                  {selectedOrder.secondary_images?.map((img, idx) => (
                    <div 
                      key={idx}
                      className={`cursor-pointer rounded border-2 ${previewImage === getImageUrl(img) ? 'border-pink-500' : 'border-gray-200'}`}
                      onClick={() => setPreviewImage(getImageUrl(img))}
                    >
                      <img src={getImageUrl(img)} alt={`Secondary ${idx + 1}`} className="w-16 h-16 object-cover rounded" />
                      <p className="text-xs text-center text-gray-500">Extra {idx + 1}</p>
                    </div>
                  ))}
                  {selectedOrder.actual_cake_image_url && (
                    <div 
                      className={`cursor-pointer rounded border-2 ${previewImage === getImageUrl(selectedOrder.actual_cake_image_url) ? 'border-green-500' : 'border-green-200'}`}
                      onClick={() => setPreviewImage(getImageUrl(selectedOrder.actual_cake_image_url))}
                    >
                      <img src={getImageUrl(selectedOrder.actual_cake_image_url)} alt="Actual" className="w-16 h-16 object-cover rounded" />
                      <p className="text-xs text-center text-green-600">Actual</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Order Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Order Details - {selectedOrder?.order_number}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Customer Name</Label>
                    <div className="font-medium mt-1">{selectedOrder.customer_info?.name}</div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="font-medium mt-1">{selectedOrder.customer_info?.phone}</div>
                  </div>
                  <div>
                    <Label>Delivery Date</Label>
                    <div className="font-medium mt-1">{selectedOrder.delivery_date}</div>
                  </div>
                  <div>
                    <Label>Delivery Time</Label>
                    <div className="font-medium mt-1">{formatTime12(selectedOrder.delivery_time)}</div>
                  </div>
                  <div>
                    <Label>Flavour</Label>
                    <div className="font-medium mt-1">{selectedOrder.flavour}</div>
                  </div>
                  <div>
                    <Label>Size</Label>
                    <div className="font-medium mt-1">{selectedOrder.size_pounds} lbs</div>
                  </div>
                  {selectedOrder.name_on_cake && (
                    <div className="col-span-2">
                      <Label>Name on Cake</Label>
                      <div className="font-medium mt-1">{selectedOrder.name_on_cake}</div>
                    </div>
                  )}
                  {selectedOrder.occasion && (
                    <div>
                      <Label>Occasion</Label>
                      <div className="font-medium mt-1">{selectedOrder.occasion}</div>
                    </div>
                  )}
                  <div>
                    <Label>Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                  </div>
                </div>

                {selectedOrder.special_instructions && (
                  <div>
                    <Label>Special Instructions</Label>
                    <div className="mt-1 p-3 bg-gray-50 rounded border">
                      <ul className="list-disc list-inside space-y-1">
                        {selectedOrder.special_instructions.split('\n').filter(line => line.trim()).map((line, idx) => (
                          <li key={idx} className="text-sm">{line.trim()}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Voice Instructions */}
                {selectedOrder.voice_instruction_url && (
                  <div>
                    <Label>🎤 Voice Instructions</Label>
                    <div className="mt-1 p-3 bg-blue-50 rounded border border-blue-200">
                      <audio 
                        controls 
                        src={getImageUrl(selectedOrder.voice_instruction_url)} 
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <Label>Payment Information</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex justify-between">
                      <span>Total Amount:</span>
                      <span className="font-bold">₹{selectedOrder.total_amount.toFixed(2)}</span>
                    </div>
                    {selectedOrder.delivery_charge > 0 && (
                      <div className="flex justify-between text-gray-500 text-sm">
                        <span>↳ Delivery Charge:</span>
                        <span>₹{selectedOrder.delivery_charge.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Paid Amount:</span>
                      <span className="font-bold text-green-600">₹{selectedOrder.paid_amount.toFixed(2)}</span>
                    </div>
                    {selectedOrder.pending_amount > 0 && (
                      <div className="flex justify-between">
                        <span>Pending Amount:</span>
                        <span className="font-bold text-orange-600">₹{selectedOrder.pending_amount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Payment - {selectedOrder?.order_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="payment-method">Payment Method *</Label>
                <Select
                  value={paymentForm.payment_method}
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="online">Online Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={submitPayment}
                className="w-full text-white"
                style={{ backgroundColor: '#e92587' }}
              >
                Record Payment
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Transfer Order Dialog */}
        <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transfer Order - {selectedOrder?.order_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Transfer this order to another outlet. All payment data will be moved with the order.
              </p>
              <div>
                <Label htmlFor="transfer-outlet">Select Target Outlet *</Label>
                <Select
                  onValueChange={(value) => submitTransfer(value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    {outlets
                      .filter(o => o.id !== selectedOrder?.outlet_id)
                      .map((outlet) => (
                        <SelectItem key={outlet.id} value={outlet.id}>
                          {outlet.name} - {outlet.city}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Upload Modal */}
        <Dialog open={photoUploadModalOpen} onOpenChange={setPhotoUploadModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Actual Cake Photo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Upload a photo of the completed cake. This will trigger incentive calculation for the sales person.
              </p>
              <div>
                <Label>Select Photo</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="cursor-pointer"
                />
              </div>
              {photoPreview && (
                <div>
                  <Label>Preview</Label>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full rounded-lg border-2 border-gray-200 mt-2"
                  />
                </div>
              )}
              <Button
                onClick={handlePhotoUpload}
                disabled={!photoFile}
                className="w-full"
                style={{ backgroundColor: '#e92587' }}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Photo & Calculate Incentive
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Order Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Order - {editFormData?.order_number}</DialogTitle>
              <DialogDescription>Update order details below</DialogDescription>
            </DialogHeader>
            {editFormData && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      value={editFormData.customer_name}
                      onChange={(e) => setEditFormData({...editFormData, customer_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Customer Phone *</Label>
                    <Input
                      value={editFormData.customer_phone}
                      onChange={(e) => setEditFormData({...editFormData, customer_phone: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Delivery Address</Label>
                  <Input
                    value={editFormData.customer_address}
                    onChange={(e) => setEditFormData({...editFormData, customer_address: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Delivery Date *</Label>
                    <Input
                      type="date"
                      value={editFormData.delivery_date}
                      onChange={(e) => setEditFormData({...editFormData, delivery_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Delivery Time * <span className="text-xs text-gray-500 font-normal">(30-min slots)</span></Label>
                    <Select
                      value={formatTime12(editFormData.delivery_time)}
                      onValueChange={(value) => setEditFormData({...editFormData, delivery_time: value})}
                    >
                      <SelectTrigger data-testid="edit-delivery-time">
                        <SelectValue placeholder="Select delivery time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {Array.from({ length: 48 }, (_, i) => {
                          const h = Math.floor(i / 2);
                          const m = (i % 2) * 30;
                          const hh = String(h).padStart(2, '0');
                          const mm = String(m).padStart(2, '0');
                          const val = `${hh}:${mm}`;
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          let h12 = h % 12;
                          if (h12 === 0) h12 = 12;
                          const label = `${h12}:${mm} ${ampm}`;
                          return (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Occasion *</Label>
                    <Select
                      value={editFormData.occasion || ''}
                      onValueChange={(value) => setEditFormData({...editFormData, occasion: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select occasion" />
                      </SelectTrigger>
                      <SelectContent>
                        {occasionsList.map((occ) => (
                          <SelectItem key={occ.id} value={occ.name}>{occ.name}</SelectItem>
                        ))}
                        {editFormData.occasion && !occasionsList.find(o => o.name === editFormData.occasion) && (
                          <SelectItem value={editFormData.occasion}>{editFormData.occasion}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Flavour *</Label>
                    <Select
                      value={
                        editFormData.flavour && !flavoursList.some(f => f.name === editFormData.flavour)
                          ? '__custom__'
                          : (editFormData.flavour || '')
                      }
                      onValueChange={(value) => {
                        if (value === '__custom__') {
                          setEditFormData({ ...editFormData, flavour: '' });
                        } else {
                          setEditFormData({ ...editFormData, flavour: value });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="edit-flavour-select">
                        <SelectValue placeholder="Select flavour" />
                      </SelectTrigger>
                      <SelectContent>
                        {flavoursList.map((flav) => (
                          <SelectItem key={flav.id} value={flav.name}>{flav.name}</SelectItem>
                        ))}
                        <SelectItem value="__custom__" data-testid="edit-flavour-custom">
                          + Custom Flavour (enter below)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {(editFormData.flavour === '' ||
                      (editFormData.flavour && !flavoursList.some(f => f.name === editFormData.flavour))) && (
                      <Input
                        className="mt-2"
                        placeholder="Enter custom flavour name"
                        value={editFormData.flavour || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, flavour: e.target.value })}
                        data-testid="edit-flavour-custom-input"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Size (Pounds) *</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editFormData.size_pounds}
                      onChange={(e) => setEditFormData({...editFormData, size_pounds: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Total Amount *</Label>
                    <Input
                      type="number"
                      value={editFormData.total_amount}
                      onChange={(e) => setEditFormData({...editFormData, total_amount: e.target.value})}
                    />
                  </div>
                </div>
                {user?.role === 'super_admin' && (
                  <div className="bg-pink-50 border border-pink-200 rounded-md p-3">
                    <Label className="text-pink-700 font-semibold">Base Size (Pounds) — Admin Only</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      placeholder="Visible only on kitchen view & KOT"
                      value={editFormData.base_size ?? ''}
                      onChange={(e) => setEditFormData({...editFormData, base_size: e.target.value})}
                      data-testid="edit-base-size-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Used by the kitchen production sheet. Hidden from the order-entry form.
                    </p>
                  </div>
                )}
                <div>
                  <Label>Name on Cake</Label>
                  <Input
                    value={editFormData.name_on_cake || ''}
                    onChange={(e) => setEditFormData({...editFormData, name_on_cake: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Special Instructions</Label>
                  <Textarea
                    rows={4}
                    value={editFormData.special_instructions || ''}
                    onChange={(e) => setEditFormData({...editFormData, special_instructions: e.target.value})}
                    placeholder={"Enter each instruction on a new line, e.g.:\nExtra cream on top\nNo fondant"}
                    data-testid="edit-special-instructions"
                  />
                </div>
                <div>
                  <Label>Cake Image</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {editFormData.cake_image_url && (
                      <img 
                        src={getImageUrl(editFormData.cake_image_url)} 
                        alt="Current" 
                        className="w-16 h-16 object-cover rounded border"
                      />
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*,.heic,.heif,image/heic,image/heif"
                        className="text-sm"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setPendingEditFile(file);
                          setEditImageEditorOpen(true);
                          e.target.value = '';
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Upload new image to replace current one</p>
                    </div>
                  </div>
                </div>

                {/* Reference / Secondary Images (up to 5) */}
                <div>
                  <Label>Reference Images (up to 5)</Label>
                  <div className="flex flex-wrap items-center gap-3 mt-1">
                    {(editFormData.secondary_images || []).map((img, idx) => (
                      <div key={idx} className="relative" data-testid={`edit-secondary-img-${idx}`}>
                        <img
                          src={getImageUrl(img)}
                          alt={`Ref ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => setEditFormData(prev => ({
                            ...prev,
                            secondary_images: (prev.secondary_images || []).filter((_, i) => i !== idx)
                          }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          data-testid={`remove-secondary-img-${idx}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {(editFormData.secondary_images || []).length < 5 && (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.heic,.heif,image/heic,image/heif"
                          className="text-sm"
                          data-testid="edit-secondary-image-input"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const res = await axios.post(`${API_URL}/api/upload-image`, fd, {
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                              });
                              const url = res.data.url || res.data.file_url;
                              setEditFormData(prev => ({
                                ...prev,
                                secondary_images: [...(prev.secondary_images || []), url].slice(0, 5)
                              }));
                              setMessage({ type: 'success', text: 'Reference image added' });
                            } catch (err) {
                              setMessage({ type: 'error', text: err.response?.data?.detail || 'Upload failed' });
                            } finally {
                              e.target.value = '';
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {`${(editFormData.secondary_images || []).length}/5 uploaded`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={submitEditOrder}
                    className="bg-pink-600 text-white hover:bg-pink-700"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Camera Capture Dialog for Ready to Deliver */}
        <Dialog open={cameraDialogOpen} onOpenChange={(open) => { if (!open) closeCameraDialog(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {deliveryChoiceStep ? 'Deliver or Customer Pickup?' : 'Capture Cake Photo'}
              </DialogTitle>
              <DialogDescription>
                {deliveryChoiceStep 
                  ? 'Photo captured! How should this order be fulfilled?' 
                  : 'Take a photo of the ready cake to mark this order as ready for delivery'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Step 1: Camera / Photo Capture */}
              {!deliveryChoiceStep && (
                <>
                  {!capturedPhoto ? (
                    <div className="relative">
                      <video 
                        id="camera-preview" 
                        autoPlay 
                        playsInline 
                        muted
                        className="w-full rounded-lg bg-black aspect-video"
                      />
                      <Button 
                        onClick={capturePhoto} 
                        className="w-full mt-3 bg-pink-600 hover:bg-pink-700 text-white"
                        data-testid="capture-photo-btn"
                      >
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Capture Photo
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <img 
                        src={capturedPhoto.url} 
                        alt="Captured cake" 
                        className="w-full rounded-lg"
                      />
                      <div className="flex gap-2 mt-3">
                        <Button 
                          variant="outline" 
                          className="flex-1" 
                          onClick={retakePhoto}
                          data-testid="retake-photo-btn"
                        >
                          Retake
                        </Button>
                        {/* Always force the 3-way choice after capturing */}
                        <Button 
                          className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" 
                          onClick={() => setDeliveryChoiceStep(true)}
                          data-testid="proceed-delivery-choice-btn"
                        >
                          Next: Choose Action
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Step 2: Deliver vs Transfer vs Pickup Choice */}
              {deliveryChoiceStep && !transferOutletStep && capturedPhoto && (
                <div>
                  <img 
                    src={capturedPhoto.url} 
                    alt="Captured cake" 
                    className="w-full rounded-lg mb-4 max-h-48 object-cover"
                  />
                  <div className="grid grid-cols-1 gap-3">
                    <Button 
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-base"
                      onClick={() => uploadCapturedAndMarkDelivery(false)}
                      disabled={uploadingPhoto}
                      data-testid="choice-deliver-btn"
                    >
                      <Truck className="h-5 w-5 mr-2" />
                      {uploadingPhoto ? 'Processing...' : 'Assign Delivery'}
                    </Button>
                    <Button 
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-base"
                      onClick={() => setTransferOutletStep(true)}
                      disabled={uploadingPhoto}
                      data-testid="choice-transfer-btn"
                    >
                      <ArrowRightLeft className="h-5 w-5 mr-2" />
                      Transfer to Another Outlet
                    </Button>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-base"
                      onClick={() => uploadCapturedAndMarkDelivery(true)}
                      disabled={uploadingPhoto}
                      data-testid="choice-pickup-btn"
                    >
                      <Package className="h-5 w-5 mr-2" />
                      {uploadingPhoto ? 'Processing...' : 'Customer Pickup at Current Outlet'}
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => setDeliveryChoiceStep(false)}
                      disabled={uploadingPhoto}
                    >
                      Back to Photo
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Pick which outlet to transfer to + delivery or pickup there */}
              {deliveryChoiceStep && transferOutletStep && capturedPhoto && (
                <div className="space-y-4">
                  <img
                    src={capturedPhoto.url}
                    alt="Captured cake"
                    className="w-full rounded-lg max-h-40 object-cover"
                  />
                  <div>
                    <Label className="text-sm font-semibold">Transfer to Outlet</Label>
                    <Select value={selectedTransferOutlet} onValueChange={setSelectedTransferOutlet}>
                      <SelectTrigger data-testid="transfer-outlet-select">
                        <SelectValue placeholder="Select target outlet" />
                      </SelectTrigger>
                      <SelectContent>
                        {outlets
                          .filter(o => o.id !== cameraOrder?.outlet_id)
                          .map(o => (
                            <SelectItem key={o.id} value={o.id}>{o.name} · {o.city}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-5"
                      onClick={() => uploadCapturedAndTransferOutlet(false)}
                      disabled={uploadingPhoto || !selectedTransferOutlet}
                      data-testid="transfer-and-deliver-btn"
                    >
                      <Truck className="h-5 w-5 mr-2" />
                      {uploadingPhoto ? 'Processing...' : 'Transfer & Deliver from There'}
                    </Button>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-5"
                      onClick={() => uploadCapturedAndTransferOutlet(true)}
                      disabled={uploadingPhoto || !selectedTransferOutlet}
                      data-testid="transfer-and-pickup-btn"
                    >
                      <Package className="h-5 w-5 mr-2" />
                      {uploadingPhoto ? 'Processing...' : 'Transfer & Customer Pickup There'}
                    </Button>
                    <Button variant="outline" onClick={() => { setTransferOutletStep(false); setSelectedTransferOutlet(''); }} disabled={uploadingPhoto}>
                      Back
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Delivery Person Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Delivery Person</DialogTitle>
              <DialogDescription>Select a delivery person for this order</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Delivery Person</Label>
                <Select value={selectedDeliveryPerson} onValueChange={setSelectedDeliveryPerson}>
                  <SelectTrigger data-testid="delivery-person-select">
                    <SelectValue placeholder="Select delivery person" />
                  </SelectTrigger>
                  <SelectContent>
                    {deliveryPersons.map((dp) => (
                      <SelectItem key={dp.id} value={dp.id}>{dp.name} ({dp.phone})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {deliveryPersons.length === 0 && (
                  <p className="text-sm text-red-500 mt-1">No delivery persons found. Add them in User Management.</p>
                )}
              </div>
              <Button 
                onClick={handleAssignDelivery} 
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={!selectedDeliveryPerson || assigning}
                data-testid="confirm-assign-btn"
              >
                <Truck className="h-4 w-4 mr-2" />
                {assigning ? 'Assigning...' : 'Assign & Dispatch'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* c6: Send cake photos to customer dialog */}
        <Dialog open={sendPhotosOpen} onOpenChange={setSendPhotosOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send Cake Photos to Customer</DialogTitle>
              <DialogDescription>
                Sends the reference image (what the customer provided) and the actual cake image
                (what your team made) to the customer on WhatsApp. Sent date defaults to today.
              </DialogDescription>
            </DialogHeader>
            {sendPhotosOrder && (
              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  Order <span className="font-semibold">#{sendPhotosOrder.order_number}</span> — {sendPhotosOrder.customer_info?.name}
                </div>
                <div>
                  <Label htmlFor="send-photos-date">Send Date</Label>
                  <Input
                    id="send-photos-date"
                    type="date"
                    value={sendPhotosDate}
                    onChange={(e) => setSendPhotosDate(e.target.value)}
                    className="mt-1"
                    data-testid="send-photos-date"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="send-photos-ref"
                    type="checkbox"
                    checked={sendPhotosIncludeRef}
                    onChange={(e) => setSendPhotosIncludeRef(e.target.checked)}
                    disabled={!sendPhotosOrder.cake_image_url}
                    data-testid="send-photos-include-ref"
                  />
                  <Label htmlFor="send-photos-ref" className="text-sm">
                    Include reference image {!sendPhotosOrder.cake_image_url && '(not available)'}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="send-photos-actual"
                    type="checkbox"
                    checked={sendPhotosIncludeActual}
                    onChange={(e) => setSendPhotosIncludeActual(e.target.checked)}
                    disabled={!sendPhotosOrder.actual_cake_image_url}
                    data-testid="send-photos-include-actual"
                  />
                  <Label htmlFor="send-photos-actual" className="text-sm">
                    Include actual cake image {!sendPhotosOrder.actual_cake_image_url && '(not available)'}
                  </Label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setSendPhotosOpen(false)}>Cancel</Button>
                  <Button
                    onClick={submitSendCakePhotos}
                    disabled={sendPhotosSending || (!sendPhotosIncludeRef && !sendPhotosIncludeActual)}
                    className="bg-pink-600 hover:bg-pink-700 text-white"
                    data-testid="send-photos-submit"
                  >
                    {sendPhotosSending ? 'Sending…' : 'Send on WhatsApp'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Group B — Add Delivery Wizard Dialog */}
        <Dialog open={addDeliveryOpen} onOpenChange={setAddDeliveryOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Delivery to Order</DialogTitle>
              <DialogDescription>
                {addDeliveryOrder?.order_number && `Order #${addDeliveryOrder.order_number} • `}
                Step {addDeliveryStep} of 4
              </DialogDescription>
            </DialogHeader>

            {/* Step 1 — Zone */}
            {addDeliveryStep === 1 && (
              <div className="space-y-4 py-2">
                <div>
                  <Label>Delivery Zone *</Label>
                  <Select
                    value={addDeliveryData.zone_id}
                    onValueChange={(v) => setAddDeliveryData({ ...addDeliveryData, zone_id: v })}
                  >
                    <SelectTrigger data-testid="add-delivery-zone-select">
                      <SelectValue placeholder="Select zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {addDeliveryZones.map((z) => (
                        <SelectItem key={z.id} value={z.id}>
                          {z.name} — ₹{z.delivery_charge}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Custom Delivery Charge</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => setAddDeliveryStep(2)}
                    disabled={!addDeliveryData.zone_id}
                    data-testid="add-delivery-step1-next"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2 — Charges */}
            {addDeliveryStep === 2 && (
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-2">
                  <input
                    id="is-complementary"
                    type="checkbox"
                    checked={addDeliveryData.is_complementary}
                    onChange={(e) => setAddDeliveryData({ ...addDeliveryData, is_complementary: e.target.checked, delivery_charge: e.target.checked ? 0 : addDeliveryData.delivery_charge })}
                    data-testid="add-delivery-complementary"
                  />
                  <Label htmlFor="is-complementary">Complementary delivery (₹0)</Label>
                </div>
                {!addDeliveryData.is_complementary && (
                  <div>
                    <Label>Delivery Charge (multiple of ₹50) *</Label>
                    <Input
                      type="number"
                      step="50"
                      min="0"
                      placeholder="e.g., 50, 100, 150"
                      value={addDeliveryData.delivery_charge}
                      onChange={(e) => setAddDeliveryData({ ...addDeliveryData, delivery_charge: e.target.value })}
                      data-testid="add-delivery-charge-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Amount must be a multiple of ₹50 or check {'"'}Complementary{'"'} above.</p>
                  </div>
                )}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setAddDeliveryStep(1)}>← Back</Button>
                  <Button
                    onClick={() => setAddDeliveryStep(3)}
                    disabled={!addDeliveryData.is_complementary && !addDeliveryData.delivery_charge}
                    data-testid="add-delivery-step2-next"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3 — Receiver */}
            {addDeliveryStep === 3 && (
              <div className="space-y-3 py-2">
                <div>
                  <Label>Receiver Name</Label>
                  <Input
                    value={addDeliveryData.receiver_name}
                    onChange={(e) => setAddDeliveryData({ ...addDeliveryData, receiver_name: e.target.value })}
                    placeholder="Same as customer if left blank"
                    data-testid="add-delivery-receiver-name"
                  />
                </div>
                <div>
                  <Label>Receiver Phone *</Label>
                  <Input
                    value={addDeliveryData.receiver_phone}
                    onChange={(e) => setAddDeliveryData({ ...addDeliveryData, receiver_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit phone"
                    data-testid="add-delivery-receiver-phone"
                  />
                </div>
                <div>
                  <Label>Delivery Address *</Label>
                  <Textarea
                    rows={2}
                    value={addDeliveryData.delivery_address}
                    onChange={(e) => setAddDeliveryData({ ...addDeliveryData, delivery_address: e.target.value })}
                    data-testid="add-delivery-address"
                  />
                </div>
                <div>
                  <Label>City *</Label>
                  <Select
                    value={addDeliveryData.delivery_city}
                    onValueChange={(v) => setAddDeliveryData({ ...addDeliveryData, delivery_city: v })}
                  >
                    <SelectTrigger data-testid="add-delivery-city-select">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {addDeliveryCities.map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {addDeliveryCities.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">No cities configured. Add cities in Settings.</p>
                  )}
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setAddDeliveryStep(2)}>← Back</Button>
                  <Button
                    onClick={() => setAddDeliveryStep(4)}
                    disabled={!addDeliveryData.receiver_phone || !addDeliveryData.delivery_address || !addDeliveryData.delivery_city}
                    data-testid="add-delivery-step3-next"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4 — Assign */}
            {addDeliveryStep === 4 && (
              <div className="space-y-4 py-2">
                <div>
                  <Label>Assign Delivery Person (optional)</Label>
                  <Select
                    value={addDeliveryData.assign_delivery_person_id}
                    onValueChange={(v) => setAddDeliveryData({ ...addDeliveryData, assign_delivery_person_id: v })}
                  >
                    <SelectTrigger data-testid="add-delivery-person-select">
                      <SelectValue placeholder="Assign now or later" />
                    </SelectTrigger>
                    <SelectContent>
                      {deliveryPersons.map((dp) => (
                        <SelectItem key={dp.id} value={dp.id}>{dp.name} ({dp.phone})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded border p-3 bg-gray-50 text-sm space-y-1">
                  <div>Zone: <strong>{addDeliveryZones.find(z => z.id === addDeliveryData.zone_id)?.name || 'Custom'}</strong></div>
                  <div>Charge: <strong>{addDeliveryData.is_complementary ? 'Complementary (₹0)' : `₹${addDeliveryData.delivery_charge}`}</strong></div>
                  <div>Receiver: <strong>{addDeliveryData.receiver_name || '—'} ({addDeliveryData.receiver_phone})</strong></div>
                  <div>Address: {addDeliveryData.delivery_address}, {addDeliveryData.delivery_city}</div>
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setAddDeliveryStep(3)}>← Back</Button>
                  <Button
                    onClick={submitAddDelivery}
                    disabled={addingDelivery}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="submit-add-delivery"
                  >
                    {addingDelivery ? 'Adding...' : 'Add Delivery'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Image editor dialog used by Edit Order's image picker */}
        <ImageEditor
          open={editImageEditorOpen}
          file={pendingEditFile}
          onCancel={() => { setEditImageEditorOpen(false); setPendingEditFile(null); }}
          onConfirm={async (blob) => {
            setEditImageEditorOpen(false);
            try {
              const formData = new FormData();
              formData.append('file', new File([blob], `edited_${Date.now()}.jpg`, { type: 'image/jpeg' }));
              const res = await axios.post(`${API_URL}/api/upload-image`, formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
              });
              const url = res.data.url || res.data.file_url;
              setEditFormData((prev) => prev ? { ...prev, cake_image_url: url } : prev);
              setMessage({ type: 'success', text: 'Image updated' });
            } catch (err) {
              setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to upload image' });
            } finally {
              setPendingEditFile(null);
            }
          }}
        />

        {/* Delete Order Reason Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!deleting) { setDeleteDialogOpen(open); if (!open) { setDeleteOrder(null); setDeleteReason(''); } } }}>
          <DialogContent className="sm:max-w-md" data-testid="delete-order-dialog">
            <DialogHeader>
              <DialogTitle>Delete Order {deleteOrder ? `#${deleteOrder.order_number}` : ''}</DialogTitle>
              <DialogDescription>
                Super Admin: deletes immediately. Others: a delete request is sent for super admin approval.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="delete-reason">Reason for deletion <span className="text-red-500">*</span></Label>
              <textarea
                id="delete-reason"
                data-testid="delete-reason-input"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g., Customer cancelled, duplicate entry, wrong details..."
                rows={4}
                autoFocus
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => { setDeleteDialogOpen(false); setDeleteOrder(null); setDeleteReason(''); }}
                disabled={deleting}
                data-testid="delete-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteOrder}
                disabled={deleting || !deleteReason.trim()}
                data-testid="delete-confirm-button"
              >
                {deleting ? 'Deleting...' : 'Delete Order'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </LayoutWithSidebar>
  );
};

export default ManageOrders;
